// Exports — what runners take away: format mix over time on one scale,
// repeat downloads, where downloads start, and runners who took the same
// plan in several formats.

import clsx from "clsx";
import { Link } from "react-router-dom";
import type { AdminExports } from "@paceroo/domain/views/admin";
import { Card, Columns, ErrorState, FormatPips, LoadingState, Note, PageHeader, SectionHeader, StatGrid } from "../components/ui";
import { useAdminQuery } from "../lib/api";
import { FORMAT_LABEL, FORMAT_TAG, FORMATS, fDT, fDay, fSpan, n0, parseWhen } from "../lib/format";
import { bucket, columns, pct } from "../lib/series";
import { useRange } from "../lib/useRange";
import { useShell } from "../shell/context";
import s from "./Exports.module.css";

export function Exports() {
  const range = useRange();
  const { bp, focusSearch } = useShell();
  const q = useAdminQuery<AdminExports>(`exports?days=${range.days}`);
  if (q.error && !q.data) return <ErrorState page="Exports" error={q.error} onRetry={q.reload} onFind={focusSearch} />;
  if (!q.data) return <LoadingState page="exports" />;

  const d = q.data;
  const mobile = bp === "mobile";
  const wide = bp === "desktop";
  const days = d.range.days;
  const header = <PageHeader kicker="Exports" before="What runners " accent="take away." />;

  if (d.analytics === "unavailable" || !d.byFormatDaily) {
    return (
      <div className={s.page}>
        {header}
        <Note>Exports are counted from usage events, which need Analytics Engine to be queryable in this environment.</Note>
      </div>
    );
  }

  // Tracked formats always show; anything else that turns up is appended.
  const formats = [...FORMATS, ...Object.keys(d.byFormatDaily).filter((f) => !(FORMATS as readonly string[]).includes(f))];
  const series = formats.map((f) => {
    const daily = d.byFormatDaily?.[f] ?? d.days.map(() => 0);
    return { format: f, total: daily.reduce((t, n) => t + n, 0), values: bucket(daily, days) };
  });
  const sharedMax = Math.max(1, ...series.flatMap((x) => x.values));
  const hist = d.histogram ?? [0, 0, 0, 0, 0];
  const histMax = Math.max(1, ...hist);
  const via = d.via ?? { app: 0, assistantLink: 0 };
  const viaTotal = via.app + via.assistantLink;
  const multi = d.multiFormat ?? [];
  const cols = wide ? "minmax(0,1.6fr) minmax(0,1.4fr) minmax(0,1.6fr) 80px 70px 100px" : "minmax(0,1.4fr) minmax(0,1fr) minmax(0,1.4fr) 72px 64px";

  return (
    <div className={s.page}>
      {header}
      {days >= 90 && (
        <Note>Raw events are kept for about 3 months, so the start of a 90-day range may be incomplete. Repeat and multi-format figures only cover what’s kept.</Note>
      )}

      <StatGrid
        cols={mobile ? 2 : 4}
        stats={[
          { label: "Exports", value: n0(d.total ?? 0), sub: "all formats" },
          { label: "Runners exporting", value: n0(d.exporters ?? 0), sub: "at least one download" },
          { label: "Repeat downloads", value: `${pct(d.repeatPairs ?? 0, d.pairs ?? 0)}%`, sub: "plans downloaded 2+ times" },
          { label: "Multi-format", value: n0(multi.length), sub: "runners, same plan, 2+ formats" },
        ]}
      />

      <Card
        head={
          <>
            <span className="kicker ink-mute">Format mix over time · same scale</span>
            <span className="t-mono-xs ink-mute">
              {fDay(d.days[0]!)} → {fDay(d.days[d.days.length - 1]!)}
            </span>
          </>
        }
      >
        <div className={s.multiples}>
          {series.map((x) => (
            <div key={x.format} className={s.multiple}>
              <div className={s.multipleHead}>
                <span className="t-label">{FORMAT_LABEL[x.format] ?? x.format}</span>
                <span className="t-mono-sm">{n0(x.total)}</span>
              </div>
              <Columns
                cols={columns([...x.values, sharedMax]).slice(0, -1)}
                height={64}
                gap={1}
                titles={x.values.map((v) => n0(v))}
              />
            </div>
          ))}
        </div>
        {d.total === 0 && <p className={clsx("t-body-sm ink-mute", s.empty)}>No exports in this range.</p>}
        <p className={clsx("t-caption ink-mute", s.caption)} style={{ marginTop: 14 }}>
          Calendar (ICS) files are made in the browser, so they aren’t counted.
        </p>
      </Card>

      <div className={s.pair}>
        <Card head={<span className="kicker ink-mute">Downloads per runner, per plan</span>}>
          <div className={s.stack}>
            <div className={s.hist}>
              {hist.map((n, i) => (
                <div key={i} className={s.histCol}>
                  <span className="t-mono-xs ink-soft">{n0(n)}</span>
                  <div className={s.histBar} style={{ height: `${n ? Math.max(4, (n / histMax) * 100) : 0}%`, background: i >= 2 ? "var(--ink)" : "var(--ink-mute)" }} />
                </div>
              ))}
            </div>
            <div className={s.histLabels}>
              {["1", "2", "3", "4", "5+"].map((k) => (
                <span key={k} className={clsx("t-mono-xs ink-mute", s.center)}>
                  {k}×
                </span>
              ))}
            </div>
            <p className={clsx("t-caption ink-mute", s.caption)}>
              Darker bars: the same plan downloaded 3+ times — often a sign the runner couldn’t find what they wanted.
            </p>
          </div>
        </Card>

        <Card head={<span className="kicker ink-mute">Where downloads start</span>}>
          <div className={s.viaStack}>
            <div className={s.viaBar}>
              <div className={s.viaApp} style={{ width: `${viaTotal ? (via.app / viaTotal) * 100 : 0}%` }} />
              <div className={s.viaLink} style={{ width: `${viaTotal ? (via.assistantLink / viaTotal) * 100 : 0}%` }} />
            </div>
            <div className={s.viaLegend}>
              <div className={s.viaItem}>
                <span className={clsx("t-label", s.viaLabel)}>
                  <span className={clsx(s.swatch, s.viaApp)} />
                  In the app
                </span>
                <span className="t-mono-md">
                  {n0(via.app)} <span className="ink-mute">{pct(via.app, viaTotal)}%</span>
                </span>
              </div>
              <div className={s.viaItem}>
                <span className={clsx("t-label", s.viaLabel)}>
                  <span className={clsx(s.swatch, s.viaLink)} />
                  Signed link (assistant)
                </span>
                <span className="t-mono-md">
                  {n0(via.assistantLink)} <span className="ink-mute">{pct(via.assistantLink, viaTotal)}%</span>
                </span>
              </div>
            </div>
            <p className={clsx("t-caption ink-mute", s.caption)}>Signed links come from get_plan_download and expire after 15 min.</p>
          </div>
        </Card>
      </div>

      <section className={s.section}>
        <SectionHeader label={`Same plan, several formats · ${n0(multi.length)} runners`} />
        {multi.length === 0 && <p className={clsx("t-body-sm ink-mute", s.none)}>Nobody downloaded one plan in two or more formats in this range.</p>}
        {multi.length > 0 && !mobile && (
          <div className={s.table}>
            <div className={s.head} style={{ gridTemplateColumns: cols }}>
              <span className="kicker ink-mute">Runner</span>
              <span className="kicker ink-mute">Plan</span>
              <span className="kicker ink-mute">Formats</span>
              <span className={clsx("kicker ink-mute", s.right)}>Downloads</span>
              <span className={clsx("kicker ink-mute", s.right)}>Within</span>
              {wide && <span className={clsx("kicker ink-mute", s.right)}>Last</span>}
            </div>
            {multi.map((m) => {
              const span = parseWhen(m.last).getTime() - parseWhen(m.first).getTime();
              return (
                <Link key={`${m.runner.id}|${m.planId}`} to={`/runners/${encodeURIComponent(m.runner.id)}`} className={s.row} style={{ gridTemplateColumns: cols }}>
                  <span className={s.who}>
                    <span className={clsx("t-label", s.ellipsis)}>{m.runner.name ?? m.runner.id}</span>
                    <span className={clsx("t-mono-xs ink-mute", s.ellipsis)}>{m.runner.email ?? "account deleted"}</span>
                  </span>
                  <span className={clsx("t-body-sm ink-soft", s.ellipsis)}>{m.planName ?? "A plan no longer on the account"}</span>
                  <FormatPips pips={pipsOf(m.formats)} />
                  <span className={clsx("t-mono-sm", s.right)}>{n0(m.downloads)}</span>
                  <span className={clsx("t-mono-sm", s.right)} style={{ color: span < 3_600_000 ? "var(--caution)" : "var(--ink-soft)" }}>
                    {fSpan(span)}
                  </span>
                  {wide && <span className={clsx("t-mono-xs ink-mute", s.right)}>{fDT(parseWhen(m.last))}</span>}
                </Link>
              );
            })}
          </div>
        )}
        {multi.length > 0 && mobile && (
          <div className={s.cards}>
            {multi.map((m) => {
              const span = parseWhen(m.last).getTime() - parseWhen(m.first).getTime();
              return (
                <Link key={`${m.runner.id}|${m.planId}`} to={`/runners/${encodeURIComponent(m.runner.id)}`} className={s.card}>
                  <span className={clsx("t-label", s.cardName)}>{m.runner.name ?? m.runner.id}</span>
                  <span className="t-body-sm ink-soft">{m.planName ?? "A plan no longer on the account"}</span>
                  <span className={s.cardPips}>
                    <FormatPips pips={pipsOf(m.formats)} />
                    <span className="t-mono-xs" style={{ color: span < 3_600_000 ? "var(--caution)" : "var(--ink-soft)" }}>
                      {n0(m.downloads)} in {fSpan(span)}
                    </span>
                  </span>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

function pipsOf(formats: Record<string, number>): Array<{ tag: string; count: number }> {
  return Object.entries(formats)
    .sort((a, b) => b[1] - a[1])
    .map(([f, count]) => ({ tag: FORMAT_TAG[f] ?? f.toUpperCase(), count }));
}
